// Smokeball panel for Call Detail. Shows on every graded call:
//   - "Already written" badge if the call has been pushed to Smokeball
//   - Otherwise: contact match (by caller phone) → pick a matter → write
//
// Master admins or domain users can write. All API errors surface as toasts.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Scale, ExternalLink, CheckCircle2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const phoneFromCall = (call) => call?.from_number || call?.caller_number || call?.phone_number || '';

export const SmokeballPanel = ({ call }) => {
  const [contacts, setContacts] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [matters, setMatters] = useState([]);
  const [loadingMatters, setLoadingMatters] = useState(false);
  const [selectedMatter, setSelectedMatter] = useState('');
  const [writing, setWriting] = useState(false);
  const [phoneQuery, setPhoneQuery] = useState(phoneFromCall(call));

  const domainId = call?.domain_id;
  const callId = call?.call_id;
  const written = !!call?.smokeball_written_at;
  const [legalGate, setLegalGate] = useState('checking'); // 'checking' | 'legal' | 'hidden'

  // Probe whether this domain supports Smokeball (legal industry gate).
  // We call the cheap settings endpoint — it returns 200 for legal domains
  // (with `configured: true/false`) and 403 for non-legal. On 403 we hide
  // the panel entirely to avoid showing the integration on dental,
  // car-dealership, IT-support etc tenants.
  const probeRef = React.useRef(false);
  useEffect(() => {
    if (probeRef.current || !domainId) return undefined;
    probeRef.current = true;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/smokeball/${domainId}/available`);
        setLegalGate(r.data?.available ? 'legal' : 'hidden');
      } catch (e) {
        // On any error (403 forbidden, network, etc.) hide the panel
        // rather than show a broken integration prompt.
        console.debug('SmokeballPanel: availability probe hid the panel', e?.response?.status || e?.message);
        setLegalGate('hidden');
      }
    }, 0);
    return () => clearTimeout(t);
  }, [domainId]);

  const runSearch = async (phone) => {
    if (!phone || !domainId) return;
    setSearching(true);
    try {
      const r = await axios.get(
        `${API}/smokeball/${domainId}/contacts/by-phone`,
        { params: { phone } },
      );
      setContacts(r.data?.contacts || []);
      if ((r.data?.contacts || []).length === 0) {
        toast.message('No matching Smokeball contact for this number.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Contact search failed');
    } finally {
      setSearching(false);
    }
  };

  // Auto-search once on mount when the call has a phone number.
  // Calling `runSearch` from inside the effect triggers state updates
  // inside an async closure (not synchronously inside the effect body),
  // which satisfies react-hooks/set-state-in-effect.
  const triggeredRef = React.useRef(false);
  useEffect(() => {
    if (triggeredRef.current) return undefined;
    if (written || !phoneQuery || !domainId) return undefined;
    triggeredRef.current = true;
    // Defer to next tick so the state writes inside runSearch happen
    // outside the synchronous effect callstack (satisfies the
    // react-hooks/set-state-in-effect rule).
    const t = setTimeout(() => runSearch(phoneQuery), 0);
    return () => clearTimeout(t);
  }, [domainId, phoneQuery, written]);

  const pickContact = async (contact) => {
    setSelectedContact(contact);
    setMatters([]);
    setSelectedMatter('');
    setLoadingMatters(true);
    try {
      const r = await axios.get(
        `${API}/smokeball/${domainId}/contacts/${contact.id}/matters`,
      );
      setMatters(r.data?.matters || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load matters');
    } finally {
      setLoadingMatters(false);
    }
  };

  const handleWrite = async () => {
    if (!selectedMatter) return;
    setWriting(true);
    try {
      const r = await axios.post(
        `${API}/smokeball/calls/${callId}/write-note`,
        { matter_id: selectedMatter },
      );
      toast.success(`Wrote call note to Smokeball matter (event ${r.data?.event_id || '—'})`);
      // Page-level fetch will re-render with the written-at timestamp.
      window.dispatchEvent(new CustomEvent('call-updated', { detail: { call_id: callId } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Write failed');
    } finally {
      setWriting(false);
    }
  };

  if (legalGate === 'checking' || legalGate === 'hidden') return null;

  if (written) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="smokeball-panel">
        <CardHeader className="flex flex-row items-center gap-2 space-y-0">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <CardTitle>Written to Smokeball</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700">
            Note written {new Date(call.smokeball_written_at).toLocaleString()} by{' '}
            <strong>{call.smokeball_written_by}</strong>
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Matter ID: <span className="font-mono">{call.smokeball_matter_id}</span>
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="smokeball-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <div className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-indigo-500" />
          <CardTitle>Write to Smokeball</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={phoneQuery}
            onChange={(e) => setPhoneQuery(e.target.value)}
            placeholder="Caller phone number"
            className="flex-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm"
            data-testid="smokeball-phone-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => runSearch(phoneQuery)}
            disabled={searching}
            data-testid="smokeball-search-btn"
          >
            {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Search
          </Button>
        </div>

        {contacts.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs uppercase text-slate-400 mb-2">Matching Contacts</p>
            <div className="space-y-2">
              {contacts.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => pickContact(c)}
                  className={`w-full flex items-center justify-between gap-2 border rounded-md px-3 py-2 text-left text-sm ${
                    selectedContact?.id === c.id
                      ? 'border-indigo-300 bg-indigo-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`smokeball-contact-${c.id}`}
                >
                  <span className="font-medium text-slate-700">{c.name || '(no name)'}</span>
                  <a
                    href={c.deep_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-slate-400 hover:text-slate-700"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedContact && (
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs uppercase text-slate-400 mb-2">Matter</p>
            {loadingMatters ? (
              <p className="text-sm text-slate-500">Loading matters…</p>
            ) : matters.length === 0 ? (
              <p className="text-sm text-slate-500">No matters found for this contact.</p>
            ) : (
              <select
                value={selectedMatter}
                onChange={(e) => setSelectedMatter(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                data-testid="smokeball-matter-select"
              >
                <option value="">Select a matter…</option>
                {matters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {(m.matter_number ? `[${m.matter_number}] ` : '') +
                      (m.description || m.id)}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {selectedMatter && (
          <Button
            type="button"
            onClick={handleWrite}
            disabled={writing}
            className="w-full"
            data-testid="smokeball-write-btn"
          >
            {writing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {writing ? 'Writing…' : 'Write call note + transcript to matter'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
